import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Mail, CheckCircle2, AlertTriangle, RefreshCw, Trash2, ArrowLeft, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const KIND_LABEL = { lead: "Lead", test: "Test", other: "Autre" };

export default function EmailLogAdmin() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ sent: 0, failed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | sent | failed
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email-log?limit=200");
      setRows(data.rows || []);
      setTotals(data.totals || { sent: 0, failed: 0, total: 0 });
    } catch (e) {
      toast.error("Impossible de charger l'historique");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const clearAll = async () => {
    if (!window.confirm("Vider l'historique des emails ? Cette action est irréversible.")) return;
    try {
      const { data } = await api.delete("/email-log");
      toast.success(`${data.deleted} entrée(s) supprimée(s)`);
      load();
    } catch { toast.error("Erreur"); }
  };

  const filtered = rows.filter(r => filter === "all" || r.status === filter);

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="admin-email-log-page">
      <header className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <Link to="/admin/settings" className="inline-flex items-center text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1 hover:underline">
            <ArrowLeft className="w-3 h-3 mr-1" /> Paramètres
          </Link>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Historique des emails</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            Tous les emails envoyés depuis ce back-office (leads, tests, notifications).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="email-log-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
          {rows.length > 0 && (
            <Button variant="outline" size="sm" onClick={clearAll} className="text-red-600 hover:text-red-700" data-testid="email-log-clear">
              <Trash2 className="w-4 h-4 mr-1" /> Vider
            </Button>
          )}
        </div>
      </header>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Total</div>
          <div className="font-display text-3xl font-bold text-[color:var(--brand-navy)] mt-1">{totals.total}</div>
        </Card>
        <Card className="p-4 border-l-4 border-emerald-500">
          <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold">Envoyés</div>
          <div className="font-display text-3xl font-bold text-emerald-700 mt-1">{totals.sent}</div>
        </Card>
        <Card className="p-4 border-l-4 border-red-500">
          <div className="text-xs uppercase tracking-wider text-red-700 font-bold">Échecs</div>
          <div className="font-display text-3xl font-bold text-red-700 mt-1">{totals.failed}</div>
        </Card>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 mb-4">
        {[
          { key: "all", label: "Tous" },
          { key: "sent", label: "Envoyés ✓" },
          { key: "failed", label: "Échecs ✗" },
        ].map(p => (
          <button
            key={p.key}
            onClick={() => setFilter(p.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              filter === p.key
                ? "bg-[color:var(--brand-navy)] text-white"
                : "bg-white border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
            }`}
            data-testid={`email-log-filter-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[hsl(var(--muted-foreground))]">
            <Mail className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {rows.length === 0 ? "Aucun email envoyé pour l'instant." : "Aucun résultat pour ce filtre."}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[hsl(var(--muted))]">
              <tr className="text-left text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">
                <th className="px-4 py-2.5">Statut</th>
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Destinataire</th>
                <th className="px-4 py-2.5">Sujet</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const ok = r.status === "sent";
                const isOpen = expanded[r.id];
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      className={`border-t border-[hsl(var(--border))] text-sm cursor-pointer hover:bg-[hsl(var(--muted))]/40 ${ok ? "" : "bg-red-50/40"}`}
                      onClick={() => r.error && setExpanded({ ...expanded, [r.id]: !isOpen })}
                      data-testid={`email-row-${r.id}`}
                    >
                      <td className="px-4 py-3">
                        {ok ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                            <CheckCircle2 className="w-4 h-4" /> Envoyé
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
                            <AlertTriangle className="w-4 h-4" /> Échec
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[hsl(var(--muted-foreground))] whitespace-nowrap">
                        {r.created_at ? new Date(r.created_at).toLocaleString("fr-FR") : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {r.kind === "test" && <FlaskConical className="w-3 h-3" />}
                          {KIND_LABEL[r.kind] || r.kind}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{r.to || "—"}</td>
                      <td className="px-4 py-3 truncate max-w-md">{r.subject || "—"}</td>
                    </tr>
                    {isOpen && r.error && (
                      <tr className="bg-red-50/30 border-t border-red-100">
                        <td colSpan={5} className="px-4 py-3 text-xs">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-red-700 mb-1">Erreur</div>
                          <code className="block bg-white border border-red-200 rounded p-2 text-red-900 whitespace-pre-wrap break-all">{r.error}</code>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
